import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ProductManager from './ProductManager';
import ProductsTableView from './ProductsTableView';
import './ProductsList.css';

function ProductsList() {
  const [products, setProducts] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [shopsWithPrices, setShopsWithPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [error, setError] = useState('');
  const [filterManufacturer, setFilterManufacturer] = useState('all');
  const [filterProductType, setFilterProductType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' oder 'table'
  const { fetchWithAuth, isAdmin } = useAuth();

  // Funktion um das richtige Emoji für Produkttypen zu bekommen
  const getProductTypeEmoji = (typeName) => {
    if (!typeName) return '📦';
    const name = typeof typeName === 'string' ? typeName : typeName.name || '';
    const lowerName = name.toLowerCase();
    
    if (lowerName.includes('beleuchtung')) return '💡';
    if (lowerName.includes('messtechnik')) return '📏';
    if (lowerName.includes('luft') || lowerName.includes('klima')) return '🌬️';
    if (lowerName.includes('zelt')) return '⛺';
    
    return '📦';
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Ein einziger API-Call statt 83+ individuelle Aufrufe
        const res = await fetch('/api/products/with-prices/all');
        const data = await res.json();
        
        const productsData = Array.isArray(data) ? data : [];
        setProducts(productsData);
        
        // Hersteller aus den Produkten extrahieren
        const mfgNames = [...new Set(productsData
          .map(p => p.manufacturerName)
          .filter(Boolean)
        )].sort();
        setManufacturers(mfgNames);

        // priceCount kommt direkt aus der Aggregation
        const shopsPerProduct = {};
        productsData.forEach(p => {
          shopsPerProduct[p._id] = p.priceCount || 0;
        });
        setShopsWithPrices(shopsPerProduct);
        
        setError('');
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Fehler beim Laden der Daten');
        setProducts([]);
        setManufacturers([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch('/api/products/with-prices/all');
      const data = await res.json();
      
      const productsData = Array.isArray(data) ? data : [];
      setProducts(productsData);
      
      const mfgNames = [...new Set(productsData
        .map(p => p.manufacturerName)
        .filter(Boolean)
      )].sort();
      setManufacturers(mfgNames);

      const shopsPerProduct = {};
      productsData.forEach(p => {
        shopsPerProduct[p._id] = p.priceCount || 0;
      });
      setShopsWithPrices(shopsPerProduct);
      
      setError('');
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Fehler beim Laden der Produkte');
      setProducts([]);
      setManufacturers([]);
    }
  };

  const handleProductUpdated = () => {
    fetchProducts();
    setSelectedProductId(null);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Dieses Produkt wirklich löschen?')) return;

    try {
      const res = await fetchWithAuth(`/api/products/${productId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        fetchProducts();
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 401) {
          setError('Nicht angemeldet. Bitte neu einloggen.');
        } else if (res.status === 403) {
          setError('Keine Admin-Berechtigung zum Löschen.');
        } else {
          setError(errorData.message || 'Fehler beim Löschen des Produkts');
        }
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Fehler beim Löschen des Produkts: ' + err.message);
    }
  };

  const handleDuplicateProduct = async (e, productId) => {
    e.stopPropagation();
    
    if (!window.confirm('Dieses Produkt duplizieren? Das neue Produkt wird offline angelegt.')) {
      return;
    }

    try {
      setError('');
      const res = await fetch(`/api/products/${productId}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        console.log('✅ Produkt dupliziert:', data.product.name);
        fetchProducts();
        setError(`✅ ${data.product.name} erfolgreich erstellt`);
        setTimeout(() => setError(''), 3000);
      } else {
        const errorData = await res.json();
        console.error('API Fehler:', errorData);
        setError(`Fehler beim Duplizieren: ${errorData.message}`);
      }
    } catch (err) {
      console.error('Error duplicating product:', err);
      setError(`Fehler beim Duplizieren: ${err.message}`);
    }
  };

  const handleToggleOnline = async (e, productId) => {
    e.stopPropagation();
    
    if (!isAdmin) {
      setError('Nur Admins können den Online-Status ändern');
      return;
    }

    try {
      setError(''); // Clear any previous errors
      
      const res = await fetchWithAuth(`/api/admin/products/${productId}/toggle-online`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        console.log('✅ Toggle erfolgreich:', data.message);
        // Lade Produkte neu
        fetchProducts();
      } else {
        const errorData = await res.text();
        console.error('API Fehler:', res.status, errorData);
        setError(`Fehler: ${res.status} - ${res.statusText}`);
      }
    } catch (err) {
      console.error('Network error toggling online status:', err);
      setError(`Netzwerkfehler: ${err.message}`);
    }
  };

  if (loading) {
    return <div className="products-loading">⏳ Produkte werden geladen...</div>;
  }

  if (error) {
    return <div className="products-error">{error}</div>;
  }

  // Wenn ein Produkt zur Bearbeitung ausgewählt wurde
  if (selectedProductId) {
    const product = products.find(p => p._id === selectedProductId);
    return (
      <div className="products-edit-view">
        <button 
          className="btn-back"
          onClick={() => setSelectedProductId(null)}
        >
          ← Zurück zur Liste
        </button>
        {product && <ProductManager productId={selectedProductId} onProductUpdated={handleProductUpdated} />}
      </div>
    );
  }

  // Produktliste anzeigen
  if (products.length === 0) {
    return <div className="no-products-manage">Noch keine Produkte vorhanden</div>;
  }

  // Unique Produkttypen für Filter sammeln
  const productTypes = [...new Set(products
    .map(p => p.productTypeName)
    .filter(Boolean)
  )].sort();

  // Produkte filtern
  const filteredProducts = products.filter(product => {
    const mfgMatch = filterManufacturer === 'all' || 
      product.manufacturerName === filterManufacturer;
    
    const typeMatch = filterProductType === 'all' || 
      product.productTypeName === filterProductType;
    
    const searchMatch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.manufacturerName && product.manufacturerName.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return mfgMatch && typeMatch && searchMatch;
  });

  // Nach Hersteller und Produktname gruppieren
  const groupedProducts = {};
  filteredProducts.forEach(product => {
    const mfg = product.manufacturerName || 'Ohne Hersteller';
    if (!groupedProducts[mfg]) {
      groupedProducts[mfg] = [];
    }
    groupedProducts[mfg].push(product);
  });

  return (
    <div className="products-list">
      {/* Filter */}
      <div className="products-filter-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Produkt oder Hersteller suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>🏭 Hersteller:</label>
          <select 
            value={filterManufacturer}
            onChange={(e) => setFilterManufacturer(e.target.value)}
            className="filter-select"
          >
            <option value="all">Alle Hersteller ({manufacturers.length})</option>
            {manufacturers.map(mfg => (
              <option key={mfg} value={mfg}>
                {mfg}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>🏷️ Produkttyp:</label>
          <select 
            value={filterProductType}
            onChange={(e) => setFilterProductType(e.target.value)}
            className="filter-select"
          >
            <option value="all">Alle Typen ({productTypes.length})</option>
            {productTypes.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-info">
          📊 {filteredProducts.length} Produkte gefunden
        </div>

        {/* View Toggle */}
        <div className="view-toggle-controls">
          <button 
            className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
            title="Kartensicht"
          >
            📋 Karten
          </button>
          <button 
            className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
            title="Tabellensicht"
          >
            📊 Tabelle
          </button>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' && (
        <ProductsTableView 
          products={filteredProducts}
          onProductSelect={setSelectedProductId}
          onDeleteProduct={handleDeleteProduct}
          onDuplicateProduct={handleDuplicateProduct}
          onToggleOnline={handleToggleOnline}
        />
      )}

      {/* Card View */}
      {viewMode === 'cards' && (
        <>
          {/* Gruppierte Produktliste */}
          {Object.entries(groupedProducts).map(([manufacturer, mfgProducts]) => (
            <div key={manufacturer} className="manufacturer-group">
              <h3 className="manufacturer-title">🏭 {manufacturer}</h3>
              <div className="products-in-group">
                {mfgProducts.map(product => {
                  const shopsCount = product.shopsCount || 0;
                  const pricesCount = shopsWithPrices[product._id] || 0;
                  
                  return (
                    <div 
                      key={product._id}
                      className="product-row"
                      onClick={() => setSelectedProductId(product._id)}
                    >
                      {/* Bild links */}
                      {product.image && <img src={product.image} alt={product.name} className="product-row-image" />}
                      
                      {/* Info Mitte */}
                      <div className="product-row-content">
                        <h3 className="product-row-name">{product.name}</h3>
                        
                        <div className="product-row-details">
                          {product.productTypeName && (
                            <span className="detail-item" style={{ color: '#4da6ff' }}>
                              {getProductTypeEmoji(product.productTypeName)} {product.productTypeName}
                            </span>
                          )}
                          <span className="detail-item" style={{ color: '#00ff88' }}>🛒 {shopsCount} Shops</span>
                          {pricesCount > 0 && (
                            <span className="detail-item" style={{ color: '#ffd700' }}>💰 {pricesCount} mit Preisen</span>
                          )}
                        </div>
                      </div>

                      {/* Buttons rechts */}
                      <div className="product-row-actions">
                        <button 
                          className={`action-btn status-btn ${product.online ? 'online' : 'offline'}`}
                          onClick={(e) => handleToggleOnline(e, product._id)}
                          title={product.online ? 'Online - klicken zum deaktivieren' : 'Offline - klicken zum aktivieren'}
                        >
                          {product.online ? '🟢 Online' : '🔴 Offline'}
                        </button>
                        <button 
                          className="action-btn duplicate-btn"
                          onClick={(e) => handleDuplicateProduct(e, product._id)}
                          title="Duplizieren"
                        >
                          📋
                        </button>
                        <button 
                          className="action-btn edit-btn"
                          onClick={() => setSelectedProductId(product._id)}
                          title="Bearbeiten"
                        >
                          ✏️
                        </button>
                        <button 
                          className="action-btn delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProduct(product._id);
                          }}
                          title="Löschen"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ProductsList;
